// Mock data for Kate Samuilova Portfolio Website

export const heroData = {
  headline: "I create scroll-stopping videos that bring customers to restaurants & beauty clinics",
  subheadline: "London-based content creator with 2+ years of experience turning social media views into real bookings through Reels, TikToks, and visual storytelling.",
  ctaButtons: [
    { text: "View Portfolio", link: "#portfolio" },
    { text: "Work With Me", link: "#contact" }
  ]
};

export const aboutData = {
  name: "Kate Samuilova",
  title: "Content Creator & Social Media Manager",
  location: "London, UK",
  bio: [
    "I'm Kate Samuilova — a London-based content creator and social media manager specialising in restaurants, cafés, hotels, and beauty clinics.",
    "With over 2 years of hands-on experience, I create scroll-stopping Reels, TikToks, and visual content that helps hospitality and beauty businesses attract more customers and increase bookings.",
    "Alongside my client work, I'm currently studying Digital Marketing — combining real-world results with strategic marketing knowledge to deliver content that doesn't just look good, but performs.",
    "My approach is simple: understand your brand, create content that resonates with your audience, and drive measurable results."
  ],
  image: "https://customer-assets.emergentagent.com/job_visuals-by-kate/artifacts/lh1hzla8_IMG_0606.jpeg"
};

export const storyData = {
  title: "How It All Started",
  paragraphs: [
    "My journey into content creation wasn't planned — it happened naturally. I've always been obsessed with visual storytelling, spending hours perfecting shots of meals, capturing café atmospheres, and experimenting with video editing.",
    "One day, I walked into a local restaurant and showed the owner what I could do. They gave me a chance. That first project led to another, then another. Each business taught me something new about what actually makes people stop scrolling and take action.",
    "Working with real restaurants and beauty clinics — not just theory or courses — shaped everything about my approach. I learned what gets engagement, what drives bookings, and what content actually converts.",
    "Now I bring that hands-on experience to every project. When you work with me, you're getting someone who's been in the trenches, creating content that delivers real results for real businesses."
  ],
  image: "https://customer-assets.emergentagent.com/job_visuals-by-kate/artifacts/yyd0ndku_IMG_4263.jpeg"
};

export const servicesData = [
  {
    id: 1,
    title: "Reels & TikTok Videos",
    subtitle: "Get discovered by new customers",
    description: "Short-form videos that stop the scroll and get your business in front of thousands of potential customers. Perfect for showcasing dishes, treatments, or your venue's atmosphere.",
    icon: "Video"
  },
  {
    id: 2,
    title: "Social Media Photography",
    subtitle: "Make your feed irresistible",
    description: "High-quality photos optimised for Instagram and social media. From food photography to interior shots — images that make people want to visit.",
    icon: "Camera"
  },
  {
    id: 3,
    title: "Social Media Management",
    subtitle: "Save time, grow faster",
    description: "I handle your content strategy, posting schedule, and engagement — so you can focus on running your business while your social media grows consistently.",
    icon: "TrendingUp"
  },
  {
    id: 4,
    title: "Brand Storytelling",
    subtitle: "Stand out from competitors",
    description: "Strategic content that communicates what makes your business special. Build an emotional connection with your audience that turns followers into loyal customers.",
    icon: "Sparkles"
  },
  {
    id: 5,
    title: "UGC Content",
    subtitle: "Authentic content that converts",
    description: "User-generated style videos perfect for ads and promotions. Authentic-feeling content that builds trust and drives more enquiries than traditional advertising.",
    icon: "Users"
  },
  {
    id: 6,
    title: "Website Design",
    subtitle: "Your 24/7 online presence",
    description: "Beautiful, modern websites that turn visitors into customers. From one-page sites to full e-commerce stores — designed to showcase your brand and drive bookings.",
    icon: "Globe",
    link: "#web-design"
  }
];

export const webDesignPackages = [
  {
    id: 1,
    name: "Starter",
    subtitle: "One-Page Website",
    price: "£249",
    priceNote: null,
    description: "Perfect for small businesses or personal brands who need a quick, professional online presence.",
    features: [
      "One scrolling page",
      "Hero section",
      "About section",
      "Services overview",
      "Portfolio preview or gallery",
      "Contact form",
      "Mobile-responsive design",
      "Basic SEO setup",
      "1 revision round"
    ],
    highlighted: false,
    cta: "Get Started",
    idealFor: "Small businesses, freelancers, personal brands"
  },
  {
    id: 2,
    name: "Business",
    subtitle: "Full Professional Website",
    price: "£549",
    priceNote: "from",
    description: "A complete website designed to attract and convert clients. Perfect for service-based businesses ready to grow.",
    features: [
      "5–10 custom pages",
      "Homepage, About, Services, Portfolio",
      "Blog & Testimonials sections",
      "Contact page with enquiry forms",
      "Mobile-first responsive design",
      "Basic SEO optimisation",
      "Social media integration",
      "2 revision rounds"
    ],
    highlighted: true,
    cta: "Most Popular",
    idealFor: "Restaurants, beauty clinics, local businesses"
  },
  {
    id: 3,
    name: "E-Commerce",
    subtitle: "Online Shop",
    price: "£999",
    priceNote: "from",
    description: "A ready-to-sell online store with clean, modern product presentation. Designed for growth.",
    features: [
      "5–10 pages",
      "Product catalogue setup",
      "Shopping basket & checkout",
      "Payment gateway integration",
      "Shipping & tax configuration",
      "Mobile-optimised store design",
      "Basic SEO setup",
      "Secure checkout experience"
    ],
    highlighted: false,
    cta: "Launch Your Store",
    idealFor: "Clothing brands, jewellery, product businesses"
  }
];

export const webDesignAddOns = [
  { name: "Website Copywriting", description: "Professional copy that converts visitors into customers" },
  { name: "Ongoing Maintenance", description: "Monthly updates, backups, and security monitoring" },
  { name: "Blog Content Setup", description: "Blog structure and initial posts to boost SEO" },
  { name: "SEO Support", description: "Advanced optimisation to help you rank higher on Google" },
  { name: "Website Updates", description: "Ongoing edits and content changes as needed" }
];

export const portfolioData = {
  videos: [
    {
      id: 1,
      title: "High Tea Elegance",
      description: "Delicate details, luxurious moments — turning high tea into viral content",
      thumbnail: "/videos/video2.mp4",
      videoUrl: "/videos/video2.mp4",
      type: "video",
      isRealVideo: true
    },
    {
      id: 2,
      title: "Macro Food Magic",
      description: "Up close and irresistible — the shots that make followers hit 'book now'",
      thumbnail: "/videos/video3.mp4",
      videoUrl: "/videos/video3.mp4",
      type: "video",
      isRealVideo: true
    },
    {
      id: 3,
      title: "The Art of Plating",
      description: "Plated to perfection — turning dishes into scroll-stopping moments",
      thumbnail: "/videos/video4.mp4",
      videoUrl: "/videos/video4.mp4",
      type: "video",
      isRealVideo: true
    },
    {
      id: 4,
      title: "Visual Narratives",
      description: "More than food — it's an experience. Content that makes people feel something",
      thumbnail: "/videos/video5.mp4",
      videoUrl: "/videos/video5.mp4",
      type: "video",
      isRealVideo: true
    },
    {
      id: 5,
      title: "Golden Hour Bakery",
      description: "Flaky pastries, golden hues — the kind of content that empties display cases",
      thumbnail: "/videos/video6.mp4",
      videoUrl: "/videos/video6.mp4",
      type: "video",
      isRealVideo: true
    },
    {
      id: 6,
      title: "Artisan Soul",
      description: "Flour dust and fresh aromas — capturing the soul of artisan baking",
      thumbnail: "/videos/video7.mp4",
      videoUrl: "/videos/video7.mp4",
      type: "video",
      isRealVideo: true
    },
    {
      id: 7,
      title: "Dough to Perfection",
      description: "Hands in dough, passion on screen — the craft that captivates audiences",
      thumbnail: "/videos/video8.mp4",
      videoUrl: "/videos/video8.mp4",
      type: "video",
      isRealVideo: true
    },
    {
      id: 8,
      title: "Before the Open Sign",
      description: "Early mornings, warm ovens — the real story behind every perfect loaf",
      thumbnail: "/videos/video9.mp4",
      videoUrl: "/videos/video9.mp4",
      type: "video",
      isRealVideo: true
    },
    {
      id: 9,
      title: "Frame by Frame",
      description: "Not just a video — a visual experience that elevates your entire brand",
      thumbnail: "/videos/video10.mp4",
      videoUrl: "/videos/video10.mp4",
      type: "video",
      isRealVideo: true
    },
    {
      id: 10,
      title: "Morning Glory",
      description: "Rise and shine content — making your morning menu the talk of the feed",
      thumbnail: "/videos/video11.mp4",
      videoUrl: "/videos/video11.mp4",
      type: "video",
      isRealVideo: true
    },
    {
      id: 11,
      title: "Teatime Stories",
      description: "Elegant, refined, shareable — content fit for the finest tea service",
      thumbnail: "/videos/video12.mp4",
      videoUrl: "/videos/video12.mp4",
      type: "video",
      isRealVideo: true
    },
    {
      id: 12,
      title: "Weekend Mood",
      description: "Where weekends come alive — content that makes every table a must-have",
      thumbnail: "/videos/video13.mp4",
      videoUrl: "/videos/video13.mp4",
      type: "video",
      isRealVideo: true
    },
    {
      id: 13,
      title: "Taste the Details",
      description: "Dripping yolks, crispy edges — the details that drive engagement",
      thumbnail: "/videos/video14.mp4",
      videoUrl: "/videos/video14.mp4",
      type: "video",
      isRealVideo: true
    },
    {
      id: 14,
      title: "Welcome In",
      description: "Doors open, vibes immaculate — making first impressions unforgettable",
      thumbnail: "/videos/video15.mp4",
      videoUrl: "/videos/video15.mp4",
      type: "video",
      isRealVideo: true
    },
    {
      id: 15,
      title: "Feel the Space",
      description: "Walk in before you walk in — content that transports viewers to your space",
      thumbnail: "/videos/video16.mp4",
      videoUrl: "/videos/video16.mp4",
      type: "video",
      isRealVideo: true
    },
    {
      id: 16,
      title: "Moody & Magnetic",
      description: "Moody, magnetic, unforgettable — when your brand deserves the spotlight",
      thumbnail: "/videos/video17.mp4",
      videoUrl: "/videos/video17.mp4",
      type: "video",
      isRealVideo: true
    },
    {
      id: 17,
      title: "Luxury Unveiled",
      description: "Clean lines, calm energy — showcasing spaces that inspire confidence",
      thumbnail: "/videos/video18.mp4",
      videoUrl: "/videos/video18.mp4",
      type: "video",
      isRealVideo: true
    },
    {
      id: 18,
      title: "Serenity in Motion",
      description: "Serenity in motion — content that makes self-care irresistible",
      thumbnail: "/videos/video19.mp4",
      videoUrl: "/videos/video19.mp4",
      type: "video",
      isRealVideo: true
    },
    {
      id: 19,
      title: "The Real Process",
      description: "Real treatments, real results — building trust one frame at a time",
      thumbnail: "/videos/video20.mp4",
      videoUrl: "/videos/video20.mp4",
      type: "video",
      isRealVideo: true
    },
    {
      id: 20,
      title: "Glow Up Journey",
      description: "The glow-up journey — content that turns viewers into clients",
      thumbnail: "/videos/video21.mp4",
      videoUrl: "/videos/video21.mp4",
      type: "video",
      isRealVideo: true
    },
    {
      id: 21,
      title: "Expert Hands",
      description: "Precision meets artistry — showcasing expertise that books itself",
      thumbnail: "/videos/video22.mp4",
      videoUrl: "/videos/video22.mp4",
      type: "video",
      isRealVideo: true
    },
    {
      id: 22,
      title: "Radiance Revealed",
      description: "Radiance captured — the transformations that speak louder than words",
      thumbnail: "/videos/video23.mp4",
      videoUrl: "/videos/video23.mp4",
      type: "video",
      isRealVideo: true
    },
    {
      id: 23,
      title: "Signature Touch",
      description: "Your best work, beautifully framed — content that converts curiosity into bookings",
      thumbnail: "/videos/video24.mp4",
      videoUrl: "/videos/video24.mp4",
      type: "video",
      isRealVideo: true
    },
    {
      id: 24,
      title: "Spotlight Moment",
      description: "Blockbuster energy for your brand — visuals that demand attention",
      thumbnail: "/videos/video25.mp4",
      videoUrl: "/videos/video25.mp4",
      type: "video",
      isRealVideo: true
    },
    {
      id: 25,
      title: "Step Inside",
      description: "Step into luxury — tours that turn first-time visitors into loyal clients",
      thumbnail: "/videos/video26.mp4",
      videoUrl: "/videos/video26.mp4",
      type: "video",
      isRealVideo: true
    },
    {
      id: 26,
      title: "Brand Authority",
      description: "Your story, your standards — content that positions you as the expert",
      thumbnail: "/videos/video27.mp4",
      videoUrl: "/videos/video27.mp4",
      type: "video",
      isRealVideo: true
    },
    {
      id: 27,
      title: "Pure Bliss",
      description: "Unwind, refresh, glow — wellness content that fills every appointment slot",
      thumbnail: "/videos/video28.mp4",
      videoUrl: "/videos/video28.mp4",
      type: "video",
      isRealVideo: true
    }
  ],
  photos: [
    {
      id: 1,
      title: "Fine Dining Presentation",
      description: "Food photography — upscale restaurant menu shoot",
      image: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0"
    },
    {
      id: 2,
      title: "Restaurant Atmosphere",
      description: "Interior photography — evening ambiance for booking page",
      image: "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4"
    },
    {
      id: 3,
      title: "Signature Dish",
      description: "Food photography — hero image for social media",
      image: "https://images.unsplash.com/photo-1600891964599-f61ba0e24092"
    },
    {
      id: 4,
      title: "Beauty Treatment Room",
      description: "Clinic photography — treatment space for website",
      image: "https://images.unsplash.com/photo-1596740926849-2d473dee8d60"
    },
    {
      id: 5,
      title: "Coffee Shop Interior",
      description: "Café photography — cosy atmosphere for Instagram grid",
      image: "https://images.unsplash.com/photo-1538333581680-29dd4752ddf2"
    },
    {
      id: 6,
      title: "Wellness Centre",
      description: "Clinic photography — aesthetic clinic brand imagery",
      image: "https://images.unsplash.com/photo-1552693673-1bf958298935"
    },
    {
      id: 7,
      title: "Brunch Spread",
      description: "Food photography — avocado toast and coffee flatlay",
      image: "https://images.pexels.com/photos/35112945/pexels-photo-35112945.jpeg"
    },
    {
      id: 8,
      title: "French Toast Special",
      description: "Restaurant content — signature brunch dish",
      image: "https://images.pexels.com/photos/31630175/pexels-photo-31630175.jpeg"
    },
    {
      id: 9,
      title: "Eggs Benedict",
      description: "Brunch photography — classic breakfast presentation",
      image: "https://images.pexels.com/photos/2122294/pexels-photo-2122294.jpeg"
    },
    {
      id: 10,
      title: "Café Morning",
      description: "Food content — latte art and pastries",
      image: "https://images.pexels.com/photos/32672206/pexels-photo-32672206.jpeg"
    },
    {
      id: 11,
      title: "Pasta Perfection",
      description: "Italian restaurant — handmade pasta showcase",
      image: "https://images.unsplash.com/photo-1473093295043-cdd812d0e601"
    },
    {
      id: 12,
      title: "Facial Treatment",
      description: "Beauty clinic — skincare treatment in progress",
      image: "https://images.pexels.com/photos/3736279/pexels-photo-3736279.jpeg"
    },
    {
      id: 13,
      title: "Aesthetic Procedure",
      description: "Clinic content — professional beauty treatment",
      image: "https://images.pexels.com/photos/4586741/pexels-photo-4586741.jpeg"
    },
    {
      id: 14,
      title: "Spa Relaxation",
      description: "Wellness photography — massage therapy session",
      image: "https://images.unsplash.com/photo-1544161515-4ab6ce6db874"
    },
    {
      id: 15,
      title: "Skincare Products",
      description: "Beauty content — product photography for clinic",
      image: "https://images.unsplash.com/photo-1570172619644-dfd03ed5d881"
    },
    {
      id: 16,
      title: "Treatment Room Setup",
      description: "Clinic interior — modern aesthetic space",
      image: "https://images.unsplash.com/photo-1629909613654-28e377c37b09"
    }
  ]
};

export const whyWorkData = [
  {
    id: 1,
    title: "Real Experience, Real Results",
    description: "2+ years creating content for actual restaurants and beauty clinics — not just theory from online courses."
  },
  {
    id: 2,
    title: "I Know What Works",
    description: "Deep understanding of social media algorithms and trends. I create content optimised for reach and engagement."
  },
  {
    id: 3,
    title: "Tailored to Your Brand",
    description: "No cookie-cutter templates. Every video and photo is customised to match your brand's unique personality and goals."
  },
  {
    id: 4,
    title: "Focus on Bookings, Not Vanity Metrics",
    description: "I care about content that drives real enquiries and customers — not just likes and followers."
  },
  {
    id: 5,
    title: "Easy to Work With",
    description: "Clear communication, reliable delivery, and a collaborative approach. I make the content process stress-free for busy business owners."
  }
];

export const trustData = {
  headline: "Trusted by Local Businesses in London",
  subheadline: "I've helped restaurants, cafés, and beauty clinics grow their online presence and attract more customers.",
  clients: [
    { name: "Bella Vista Restaurant", type: "Italian Restaurant" },
    { name: "Glow Aesthetics", type: "Beauty Clinic" },
    { name: "The Corner Café", type: "Coffee Shop" },
    { name: "Mayfair Beauty", type: "Aesthetic Clinic" },
    { name: "Luna Bistro", type: "Restaurant" },
    { name: "Radiant Skin Clinic", type: "Beauty Clinic" }
  ]
};

export const contactData = {
  title: "Let's Work Together",
  subtitle: "Ready to get more customers through social media?",
  description: "Whether you're a restaurant looking to fill more tables or a beauty clinic wanting to book more treatments, I'd love to help. Let's chat about how we can make your social media work harder for your business.",
  cta: "Let's create content that brings in customers"
};

export const navigationLinks = [
  { label: "About", href: "#about" },
  { label: "Services", href: "#services" },
  { label: "Portfolio", href: "#portfolio" },
  { label: "Pricing", href: "#pricing" },
  { label: "Blog", href: "#blog" },
  { label: "Contact", href: "#contact" }
];

export const socialLinks = [
  { platform: "Instagram", url: "https://instagram.com/katesamuilova" },
  { platform: "TikTok", url: "https://tiktok.com/@katesamuilova" },
  { platform: "LinkedIn", url: "https://linkedin.com" }
];

export const testimonialsData = [
  {
    id: 1,
    name: "Sofia Martinez",
    role: "Owner",
    business: "Bella Vista Restaurant",
    image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200&q=80",
    quote: "Kate transformed our social media presence completely. Our bookings increased by 40% within the first two months of working together.",
    rating: 5
  },
  {
    id: 2,
    name: "James Chen",
    role: "Marketing Director",
    business: "Glow Aesthetics Clinic",
    image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200&q=80",
    quote: "The content Kate creates is exactly what our brand needed. Professional, engaging, and perfectly aligned with our aesthetic.",
    rating: 5
  },
  {
    id: 3,
    name: "Emma Thompson",
    role: "Founder",
    business: "The Corner Café",
    image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=200&q=80",
    quote: "Working with Kate felt like having an in-house creative team. She understood our vision from day one and delivered beyond expectations.",
    rating: 5
  },
  {
    id: 4,
    name: "Michael Roberts",
    role: "General Manager",
    business: "Mayfair Boutique Hotel",
    image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&q=80",
    quote: "Kate's videos capture the essence of luxury hospitality. Our engagement rates have never been higher.",
    rating: 5
  }
];

export const blogCategories = [
  { id: "all", label: "All Posts" },
  { id: "content-tips", label: "Content Tips for Businesses" },
  { id: "behind-the-scenes", label: "Behind the Scenes" },
  { id: "case-studies", label: "Case Studies" },
  { id: "trends", label: "Social Media Trends" },
  { id: "ugc-education", label: "UGC & Content Creation Education" },
  { id: "my-journey", label: "My Journey" }
];

export const blogData = [
  {
    id: 1,
    slug: "how-restaurants-attract-customers-instagram-reels",
    title: "How Restaurants Can Attract More Customers with Instagram Reels",
    excerpt: "Discover the proven strategies that help restaurants increase foot traffic and bookings through engaging Instagram Reels content.",
    category: "content-tips",
    categoryLabel: "Content Tips for Businesses",
    date: "January 15, 2025",
    readTime: "6 min read",
    image: "https://images.unsplash.com/photo-1600891964599-f61ba0e24092?w=800&q=80",
    featured: true,
    content: [
      {
        type: "paragraph",
        text: "In today's digital-first world, Instagram Reels have become one of the most powerful tools for restaurants looking to attract new customers. With the algorithm favouring short-form video content, there's never been a better time to leverage this format for your restaurant's marketing."
      },
      {
        type: "heading",
        text: "Why Reels Work for Restaurants"
      },
      {
        type: "paragraph",
        text: "Food is inherently visual. The sizzle of a steak, the stretch of melted cheese, the artful plating of a signature dish – these moments are made for video. Reels allow you to capture these sensory experiences in a way that static photos simply can't match."
      },
      {
        type: "paragraph",
        text: "The Instagram algorithm also heavily promotes Reels to non-followers, meaning your content can reach potential customers who've never heard of your restaurant before. This organic reach is invaluable for local businesses."
      },
      {
        type: "heading",
        text: "5 Reel Ideas That Drive Bookings"
      },
      {
        type: "list",
        items: [
          "Behind-the-scenes kitchen prep – Show the passion and skill behind your dishes",
          "Dish reveals with satisfying reveals – The classic 'plate drop' never fails",
          "Chef storytelling – Let your chef's personality shine through",
          "Customer reactions (with permission) – Social proof in action",
          "Seasonal menu teasers – Create anticipation for new offerings"
        ]
      },
      {
        type: "heading",
        text: "Timing and Consistency"
      },
      {
        type: "paragraph",
        text: "Post when your audience is hungry – literally. Lunch content performs best between 11am-1pm, while dinner content peaks between 5pm-7pm. Consistency matters more than frequency; aim for 3-4 quality Reels per week rather than daily mediocre content."
      },
      {
        type: "heading",
        text: "The Bottom Line"
      },
      {
        type: "paragraph",
        text: "Restaurants that embrace Reels see an average 40% increase in profile visits and a significant boost in reservation enquiries. The key is authenticity – show the real people, real food, and real atmosphere that make your restaurant special."
      }
    ]
  },
  {
    id: 2,
    slug: "why-beauty-clinic-needs-short-form-video",
    title: "Why Your Beauty Clinic Needs Short-Form Video Content",
    excerpt: "Video content builds trust faster than any other medium. Learn how to showcase your treatments effectively and convert viewers into clients.",
    category: "content-tips",
    categoryLabel: "Content Tips for Businesses",
    date: "January 8, 2025",
    readTime: "5 min read",
    image: "https://images.unsplash.com/photo-1552693673-1bf958298935?w=800&q=80",
    featured: false,
    content: [
      {
        type: "paragraph",
        text: "The beauty and aesthetics industry thrives on trust. Clients are making decisions about procedures that affect their appearance, and they need to feel confident in their choice of clinic. Short-form video content is the fastest way to build that trust."
      },
      {
        type: "heading",
        text: "The Trust Factor"
      },
      {
        type: "paragraph",
        text: "Before booking a treatment, potential clients want to know: What does the clinic look like? Who will be treating me? What can I expect during the procedure? Video answers all these questions in seconds, creating familiarity before they even walk through your door."
      },
      {
        type: "heading",
        text: "Content Ideas for Beauty Clinics"
      },
      {
        type: "list",
        items: [
          "Treatment process videos (with client consent)",
          "Before and after transformations",
          "Practitioner introductions and qualifications",
          "Clinic tour and atmosphere showcase",
          "Educational content about treatments",
          "Myth-busting common misconceptions"
        ]
      },
      {
        type: "heading",
        text: "Addressing Client Concerns Through Video"
      },
      {
        type: "paragraph",
        text: "Many potential clients have concerns about pain, recovery time, or results. Video content that honestly addresses these concerns – showing real procedures and real results – converts better than any polished advertisement."
      },
      {
        type: "paragraph",
        text: "The clinics I work with typically see a 60% increase in consultation bookings within the first three months of consistent video content. The ROI speaks for itself."
      }
    ]
  },
  {
    id: 3,
    slug: "what-happens-professional-content-creation-day",
    title: "What Happens During a Professional Content Creation Day",
    excerpt: "Ever wondered what goes into a content shoot? Here's a behind-the-scenes look at how I create scroll-stopping content for brands.",
    category: "behind-the-scenes",
    categoryLabel: "Behind the Scenes",
    date: "January 2, 2025",
    readTime: "7 min read",
    image: "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=800&q=80",
    featured: false,
    content: [
      {
        type: "paragraph",
        text: "One of the questions I get asked most often is: 'What actually happens during a content creation day?' Today, I'm pulling back the curtain to show you exactly what goes into creating professional content for restaurants and beauty brands."
      },
      {
        type: "heading",
        text: "Pre-Production (The Day Before)"
      },
      {
        type: "paragraph",
        text: "Great content starts before the camera even comes out. The day before a shoot, I'm finalising shot lists, checking equipment, and confirming all details with the client. For restaurants, this means knowing which dishes we're featuring and when they'll be freshest."
      },
      {
        type: "heading",
        text: "The Morning: Setup and Preparation"
      },
      {
        type: "paragraph",
        text: "I typically arrive 30-60 minutes before we start shooting. This time is crucial for assessing the lighting, setting up equipment, and doing a walkthrough with the client. Natural light is my best friend, so timing matters."
      },
      {
        type: "heading",
        text: "The Shoot: Capturing the Magic"
      },
      {
        type: "paragraph",
        text: "A typical content day runs 3-5 hours, during which I'll capture 10-15 different pieces of content. This includes various angles, formats (vertical and horizontal), and styles to give the client maximum flexibility."
      },
      {
        type: "heading",
        text: "Post-Production: Where It All Comes Together"
      },
      {
        type: "paragraph",
        text: "After the shoot, I spend 2-3 days editing, colour grading, and adding music and captions. This is where raw footage transforms into scroll-stopping content. Every transition, every cut, every sound effect is intentional."
      }
    ]
  },
  {
    id: 4,
    slug: "reels-vs-tiktok-local-businesses",
    title: "Reels vs TikTok: What Works Best for Local Businesses",
    excerpt: "Both platforms offer incredible reach, but which one is right for your business? A detailed comparison based on real results.",
    category: "trends",
    categoryLabel: "Social Media Trends",
    date: "December 28, 2024",
    readTime: "8 min read",
    image: "https://images.unsplash.com/photo-1538333581680-29dd4752ddf2?w=800&q=80",
    featured: false,
    content: [
      {
        type: "paragraph",
        text: "The 'Reels vs TikTok' debate is one I hear constantly from business owners. Both platforms offer massive organic reach potential, but they serve different purposes and audiences. Let me break down what I've learned from creating content for both."
      },
      {
        type: "heading",
        text: "Audience Demographics"
      },
      {
        type: "paragraph",
        text: "TikTok skews younger (16-34), while Instagram Reels reaches a broader age range including the 25-45 demographic – often the key spending demographic for restaurants and beauty clinics. For local businesses, this matters."
      },
      {
        type: "heading",
        text: "Content Style Differences"
      },
      {
        type: "list",
        items: [
          "TikTok rewards raw, authentic, trend-driven content",
          "Reels can be more polished and brand-aligned",
          "TikTok's algorithm is more aggressive with new content",
          "Reels benefit from existing Instagram followers"
        ]
      },
      {
        type: "heading",
        text: "My Recommendation for Local Businesses"
      },
      {
        type: "paragraph",
        text: "Start with Instagram Reels if you already have an Instagram presence. The platform offers better integration with your existing profile, Stories, and booking features. TikTok is excellent for brand awareness but Instagram typically drives more direct conversions for local businesses."
      },
      {
        type: "paragraph",
        text: "The best strategy? Create content that works for both. Film once, edit twice, post everywhere."
      }
    ]
  },
  {
    id: 5,
    slug: "ugc-content-why-it-converts-better-than-ads",
    title: "UGC Content: Why It Converts Better Than Traditional Ads",
    excerpt: "User-generated content outperforms traditional advertising by 4x. Here's why, and how your brand can leverage it.",
    category: "ugc-education",
    categoryLabel: "UGC & Content Creation Education",
    date: "December 20, 2024",
    readTime: "6 min read",
    image: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800&q=80",
    featured: false,
    content: [
      {
        type: "paragraph",
        text: "Traditional advertising is losing its effectiveness. Consumers have developed 'ad blindness' – they scroll past polished advertisements without a second glance. But user-generated content (UGC) stops them in their tracks. Here's why."
      },
      {
        type: "heading",
        text: "What Makes UGC So Effective?"
      },
      {
        type: "paragraph",
        text: "UGC feels authentic because it is. When content looks like it was created by a real person rather than a brand's marketing team, viewers let their guard down. This authenticity builds trust faster than any corporate messaging."
      },
      {
        type: "heading",
        text: "The Statistics Don't Lie"
      },
      {
        type: "list",
        items: [
          "UGC generates 4x higher click-through rates",
          "79% of people say UGC impacts their purchasing decisions",
          "Ads featuring UGC see 50% lower cost-per-click",
          "UGC-based campaigns see 29% higher web conversions"
        ]
      },
      {
        type: "heading",
        text: "How to Use UGC for Your Brand"
      },
      {
        type: "paragraph",
        text: "The key is to create content that doesn't look like content. It should feel native to the platform – like something a friend might post after visiting your restaurant or trying your treatment. This is exactly what I specialise in creating for brands."
      }
    ]
  },
  {
    id: 6,
    slug: "from-hobby-to-career-my-content-creation-journey",
    title: "From Hobby to Career: My Content Creation Journey",
    excerpt: "How I turned my passion for visual storytelling into a thriving content creation business, and the lessons I learned along the way.",
    category: "my-journey",
    categoryLabel: "My Journey",
    date: "December 15, 2024",
    readTime: "9 min read",
    image: "https://customer-assets.emergentagent.com/job_visuals-by-kate/artifacts/yyd0ndku_IMG_4263.jpeg",
    featured: false,
    content: [
      {
        type: "paragraph",
        text: "Two years ago, I was creating content purely for fun – capturing meals at restaurants I loved, experimenting with editing techniques, building my own social media presence. I never imagined it would become my career."
      },
      {
        type: "heading",
        text: "The Turning Point"
      },
      {
        type: "paragraph",
        text: "It started when a local café owner saw my posts and asked if I could create similar content for them. That first project taught me more than any course ever could. I learned about client communication, managing expectations, and delivering professional results."
      },
      {
        type: "heading",
        text: "Building My Niche"
      },
      {
        type: "paragraph",
        text: "I quickly realised that specialising was the key to standing out. Instead of being a generalist content creator, I focused exclusively on hospitality and beauty – industries I genuinely loved and understood. This focus allowed me to develop deep expertise and build a reputation."
      },
      {
        type: "heading",
        text: "Lessons Learned"
      },
      {
        type: "list",
        items: [
          "Your personal projects are your best portfolio",
          "Specialisation beats generalisation",
          "Underpromise and overdeliver – always",
          "Client relationships matter more than viral content",
          "Consistency compounds over time"
        ]
      },
      {
        type: "heading",
        text: "What's Next"
      },
      {
        type: "paragraph",
        text: "I'm more passionate about this work than ever. Every project teaches me something new, and every successful campaign for a client reinforces why I chose this path. If you're considering content creation as a career, my biggest advice is: start now, with whatever you have."
      }
    ]
  },
  {
    id: 7,
    slug: "bella-vista-case-study-40-percent-booking-increase",
    title: "Case Study: How Bella Vista Increased Bookings by 40%",
    excerpt: "A deep dive into the content strategy that transformed a new restaurant's social media presence and drove real business results.",
    category: "case-studies",
    categoryLabel: "Case Studies",
    date: "December 10, 2024",
    readTime: "10 min read",
    image: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800&q=80",
    featured: false,
    content: [
      {
        type: "paragraph",
        text: "When Bella Vista, a new Italian restaurant in Shoreditch, approached me, they had a beautiful space, amazing food, but barely any online presence. Three months later, they'd become one of the most talked-about restaurants in the neighbourhood. Here's how we did it."
      },
      {
        type: "heading",
        text: "The Challenge"
      },
      {
        type: "paragraph",
        text: "Bella Vista was competing in one of London's most saturated restaurant markets. They needed to stand out quickly, build awareness locally, and convert social media attention into actual bookings."
      },
      {
        type: "heading",
        text: "The Strategy"
      },
      {
        type: "paragraph",
        text: "We focused on three content pillars: the food (obviously), the story behind the restaurant, and the team's personality. Every piece of content served one of these pillars, creating a cohesive brand narrative."
      },
      {
        type: "heading",
        text: "The Content Mix"
      },
      {
        type: "list",
        items: [
          "8 Reels per month showcasing signature dishes",
          "Weekly behind-the-scenes Stories",
          "Monthly chef interview content",
          "User-generated content reposts (with permission)",
          "Seasonal menu launch campaigns"
        ]
      },
      {
        type: "heading",
        text: "The Results"
      },
      {
        type: "paragraph",
        text: "Within three months: 15,000 new followers, 2.5 million total video views, 8.5% engagement rate, and most importantly – a 40% increase in bookings directly attributed to social media discovery. The investment in content paid for itself within the first month."
      }
    ]
  },
  {
    id: 8,
    slug: "5-tiktok-trends-restaurants-2025",
    title: "5 TikTok Trends Every Restaurant Should Try in 2025",
    excerpt: "From ASMR cooking to POV content, these are the trends driving engagement for food brands right now.",
    category: "trends",
    categoryLabel: "Social Media Trends",
    date: "December 5, 2024",
    readTime: "5 min read",
    image: "https://images.unsplash.com/photo-1600891964599-f61ba0e24092?w=800&q=80",
    featured: false,
    content: [
      {
        type: "paragraph",
        text: "Social media trends move fast, but staying ahead of them can give your restaurant a significant competitive advantage. Here are the five trends I'm seeing drive the most engagement for food brands in 2025."
      },
      {
        type: "heading",
        text: "1. ASMR Cooking Content"
      },
      {
        type: "paragraph",
        text: "The sounds of cooking – sizzling, chopping, pouring – are incredibly satisfying. Videos that highlight these sounds (with minimal or no music) are performing exceptionally well, especially for comfort food and high-end dining."
      },
      {
        type: "heading",
        text: "2. POV Customer Experience"
      },
      {
        type: "paragraph",
        text: "First-person videos that simulate the experience of visiting your restaurant help viewers imagine themselves there. Walking through the door, being seated, seeing the menu, watching food arrive – it's immersive storytelling."
      },
      {
        type: "heading",
        text: "3. Staff Personality Content"
      },
      {
        type: "paragraph",
        text: "People connect with people, not brands. Content featuring your staff's personalities, humour, and expertise humanises your restaurant and creates emotional connections."
      },
      {
        type: "heading",
        text: "4. Behind-the-Scenes Prep"
      },
      {
        type: "paragraph",
        text: "The work that goes into each service – morning prep, mise en place, team briefings – fascinates food lovers. It showcases professionalism and passion."
      },
      {
        type: "heading",
        text: "5. Trending Audio Integration"
      },
      {
        type: "paragraph",
        text: "Using trending sounds and music can dramatically increase your content's reach. The key is adapting trends to fit your brand authentically rather than forcing it."
      }
    ]
  }
];

export const pricingData = [
  {
    id: 1,
    name: "Starter",
    subtitle: "Perfect for small businesses",
    price: "£99",
    period: "per month",
    description: "Essential content creation to kickstart your social media presence.",
    features: [
      "4 Reels/TikToks per month",
      "8 Static posts",
      "Basic editing & captions",
      "1 revision round",
      "Content calendar"
    ],
    highlighted: false,
    cta: "Get Started"
  },
  {
    id: 2,
    name: "Growth",
    subtitle: "Most popular choice",
    price: "£249",
    period: "per month",
    description: "Comprehensive content strategy for brands ready to scale.",
    features: [
      "8 Reels/TikToks per month",
      "16 Static posts",
      "Professional editing",
      "2 revision rounds",
      "Content calendar & strategy",
      "Hashtag research",
      "Monthly analytics report"
    ],
    highlighted: true,
    cta: "Start Growing"
  },
  {
    id: 3,
    name: "Premium",
    subtitle: "Full-service solution",
    price: "£749",
    period: "per month",
    description: "Complete social media management and content creation.",
    features: [
      "12 Reels/TikToks per month",
      "Unlimited static posts",
      "Premium editing & effects",
      "Unlimited revisions",
      "Full strategy & planning",
      "Community management",
      "Weekly analytics & calls",
      "Priority support"
    ],
    highlighted: false,
    cta: "Go Premium"
  }
];

export const faqData = [
  {
    id: 1,
    question: "What types of businesses do you work with?",
    answer: "I specialise in working with restaurants, cafés, hotels, and beauty/aesthetic clinics. However, I'm open to working with any lifestyle or hospitality brand that values high-quality visual content."
  },
  {
    id: 2,
    question: "How does the content creation process work?",
    answer: "The process starts with a discovery call where we discuss your brand, goals, and content needs. Then I create a content strategy and calendar. For shoots, I'll either come to your location or we can arrange a studio session. You'll receive the final edited content within 5-7 business days."
  },
  {
    id: 3,
    question: "Do you offer one-time projects or only monthly retainers?",
    answer: "Both! While monthly retainers offer the best value and consistency for your brand, I also offer one-time project packages for launches, events, or specific campaigns. Get in touch to discuss your needs."
  },
  {
    id: 4,
    question: "What equipment do you use?",
    answer: "I shoot with professional mirrorless cameras and cinema lenses, along with professional lighting equipment. For certain content styles, I also use high-end smartphones to achieve that authentic, native social media look."
  },
  {
    id: 5,
    question: "How many revisions are included?",
    answer: "The number of revisions depends on your package. Starter includes 1 round, Growth includes 2 rounds, and Premium offers unlimited revisions. I always aim to nail the brief on the first attempt through thorough pre-production planning."
  },
  {
    id: 6,
    question: "Can you help with social media strategy, not just content?",
    answer: "Absolutely! My Growth and Premium packages include strategy services. I can help with content planning, posting schedules, hashtag strategies, and performance analysis to ensure your content drives real results."
  },
  {
    id: 7,
    question: "What's your turnaround time?",
    answer: "Standard turnaround is 5-7 business days for edited content. Rush delivery is available for an additional fee. For monthly retainers, we'll establish a content calendar that ensures you always have fresh content ready to post."
  },
  {
    id: 8,
    question: "Do you travel for shoots?",
    answer: "Yes! I'm based in London but regularly travel across the UK for shoots. Travel costs may apply for locations outside Greater London. International projects can also be arranged."
  }
];

export const caseStudiesData = [
  {
    id: 1,
    title: "Bella Vista Restaurant",
    subtitle: "Italian Fine Dining",
    category: "Restaurant",
    image: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800&q=80",
    challenge: "A new Italian restaurant in Shoreditch struggling to build awareness and attract diners in a competitive market.",
    solution: "Created a series of behind-the-scenes kitchen reels, chef storytelling content, and mouth-watering food videos optimised for Instagram and TikTok.",
    results: [
      { metric: "40%", label: "Increase in bookings" },
      { metric: "15K", label: "New followers in 3 months" },
      { metric: "2.5M", label: "Total video views" },
      { metric: "8.5%", label: "Engagement rate" }
    ],
    testimonial: "Kate's content made our restaurant the talk of the neighbourhood.",
    duration: "3 months"
  },
  {
    id: 2,
    title: "Glow Aesthetics Clinic",
    subtitle: "Premium Beauty Treatments",
    category: "Beauty",
    image: "https://images.unsplash.com/photo-1552693673-1bf958298935?w=800&q=80",
    challenge: "A beauty clinic wanting to showcase their treatments and build trust with potential clients through social media.",
    solution: "Produced educational content about treatments, client transformation stories, and clinic tour videos that highlighted the premium experience.",
    results: [
      { metric: "60%", label: "More consultation bookings" },
      { metric: "10K", label: "Followers gained" },
      { metric: "1.8M", label: "Video impressions" },
      { metric: "35%", label: "Website traffic increase" }
    ],
    testimonial: "Our clients now come in already trusting us because of the content they've seen.",
    duration: "4 months"
  },
  {
    id: 3,
    title: "The Corner Café",
    subtitle: "Specialty Coffee & Brunch",
    category: "Café",
    image: "https://images.unsplash.com/photo-1538333581680-29dd4752ddf2?w=800&q=80",
    challenge: "A local café wanting to stand out from chain competitors and build a loyal community of regulars.",
    solution: "Focused on authentic, warm content showcasing the café's personality, barista skills, and cosy atmosphere through day-in-the-life style videos.",
    results: [
      { metric: "50%", label: "Increase in foot traffic" },
      { metric: "8K", label: "Local followers" },
      { metric: "500K", label: "Monthly reach" },
      { metric: "12%", label: "Engagement rate" }
    ],
    testimonial: "We've become the go-to spot in the neighbourhood thanks to Kate's content.",
    duration: "2 months"
  }
];
